import { EditorEvents } from '@/modules/Editor/eventBus'
import type { Editor } from '@/modules/Editor/editor'
import { resume as resumeClicker } from '@/modules/Editor/plugins/click/resume'
import { deleteVertex } from '@/modules/Editor/plugins/edit/deleteVertex'
import { toggleReferenceLines } from '@/modules/Editor/plugins/referenceLines/toggleReferenceLines'
import { useWindowLevelTabInteractions } from '@/modules/Editor/plugins/windowLevel/useWindowLevelTabInteractions'
import { useZoomInteractions } from '@/modules/Editor/plugins/zoom/useZoomInteractions'

export const PluginName = {
  Clicker: 'clicker',
  Clipboard: 'clipboard',
  Edit: 'edit',
  ReferenceLines: 'reference_lines',
  SaveAnnotations: 'save_annotations',
} as const

export type PluginName = (typeof PluginName)[keyof typeof PluginName]

export class PluginManager {
  private pluginNames: PluginName[] = []

  constructor(editor: Editor) {
    // interactions conditioned to the presence of a plugin

    const registerInteraction = (command: string, name: PluginName, callback: () => void): void =>
      editor.registerCommand(command, () => this.pluginNames.includes(name) && callback())

    editor.registerCommand('clicker_tool.resume', (isManual?: boolean) => {
      if (this.pluginNames.includes(PluginName.Clicker)) {
        resumeClicker(editor, isManual)
      }
    })

    registerInteraction('save_annotations.toast', PluginName.SaveAnnotations, () => {
      EditorEvents.message.emit({ content: 'No need, your work is auto-saved', level: 'info' })
    })

    registerInteraction('reference_lines.toggle', PluginName.ReferenceLines, () =>
      toggleReferenceLines(editor),
    )

    registerInteraction('edit.delete', PluginName.Edit, () => deleteVertex(editor))

    // always available interactions

    const zoom = useZoomInteractions(editor)
    editor.registerCommand('zoom.reset', zoom.reset)
    editor.registerCommand('zoom.focus_on_selected_annotation', zoom.focusOnSelectedAnnotation)
    editor.registerCommand('zoom.in', zoom.zoomIn)
    editor.registerCommand('zoom.out', zoom.zoomOut)

    const windowLevel = useWindowLevelTabInteractions(editor)
    editor.registerCommand('window_level.activate', windowLevel.activate)
    editor.registerCommand('window_level.deactivate', windowLevel.deactivate)
  }

  public setPlugins(pluginNames: PluginName[]): void {
    this.pluginNames = pluginNames
  }

  public clearPlugins(): void {
    this.pluginNames = []
  }
}
